// Sites view module: World Heritage wonders and resource deposits on the map. Owned by view-sites.
//
//   snapshot.wonders ─► plan.pickWonders ─┬─► props  (monument models on a few sites, close zoom)
//   snapshot.deposits ─► plan.groupDeposits ┼─► chips  (round icons; a modelled wonder's chip steps aside)
//   snapshot.cities (hexes to keep clear)   └─► danger (UNESCO-blue dashed ring, danger-listed hexes)
//
// Truth: a marker exists only where a record does, and `unsourced()` counts the markers drawn
// (model or chip) whose record has no source id.

import type { WorldSnapshot } from '../../world/api.ts';
import type { ViewCore } from '../index.ts';
import type { ViewModule } from '../modules.ts';
import { HEX_KIND } from '../terrain/heights.ts';
import { DangerRing } from './danger.ts';
import { SiteChips } from './icons.ts';
import { groupDeposits, pickWonders, type ResourceView } from './plan.ts';
import { SiteProps } from './props.ts';

/** Lens id of the resources lens (world/lenses.ts): icons and props show at any zoom under it. */
const RESOURCES_LENS = 'resources';

function createSites(): ViewModule {
  let core: ViewCore | null = null;
  let props: SiteProps | null = null;
  let chips: SiteChips | null = null;
  let danger: DangerRing | null = null;
  let detach: Array<() => void> = [];
  let view: ResourceView = 'plain';
  let built = '';

  /** Every frame after the camera moved: choose models, then place chips. */
  function frame(c: ViewCore): void {
    const pose = c.camera.pose();
    props?.update(c.camera.camera, pose.x, pose.height, view);
    chips?.update(pose.x, pose.height);
  }

  /** Hexes of the cities: their buildings fill the middle, so models stand aside. */
  function cityHexes(snapshot: WorldSnapshot): Set<number> {
    return new Set(snapshot.cities.map((city) => city.hex));
  }

  /** What the built sites depend on; equal keys mean nothing needs rebuilding. */
  function signature(snapshot: WorldSnapshot, cities: ReadonlySet<number>): string {
    const wonders = snapshot.wonders;
    let citySum = 0;
    for (const hex of cities) {
      citySum += hex;
    }
    return [
      snapshot.date.slice(0, 4),
      wonders.length,
      wonders[0]?.id,
      wonders[wonders.length - 1]?.id,
      snapshot.deposits?.length ?? 0,
      cities.size,
      citySum,
    ].join('|');
  }

  return {
    name: 'sites',
    async install(c) {
      core = c;
      danger = new DangerRing();
      detach.push(c.surface.register(danger.layer));

      props = await SiteProps.create(c);
      c.engine.scene.add(props.group);
      chips = new SiteChips(c);
      detach.push(c.engine.onFrame(() => frame(c)));
    },
    warm(x, y, z) {
      props?.warm(x, y, z);
    },
    setSnapshot(snapshot) {
      if (!core || !props || !chips || !danger) {
        return;
      }

      const cities = cityHexes(snapshot);
      const key = signature(snapshot, cities);
      if (key === built) {
        return;
      }
      built = key;

      const wonders = pickWonders(snapshot.wonders, snapshot.date);
      const deposits = groupDeposits(snapshot.deposits ?? []);
      const modelled = props.setSites(wonders, deposits, cities);
      chips.setSites(wonders, deposits, modelled);

      // The ring is a ground layer: land and lake hexes only (the ocean is its own mesh).
      const shapes = core.shapes;
      danger.setHexes(
        wonders
          .filter((w) => w.danger)
          .map((w) => w.hex)
          .filter((hex) => shapes.kind[hex] === HEX_KIND.LAND || shapes.kind[hex] === HEX_KIND.LAKE),
      );
    },
    setLens(result) {
      view = result?.id === RESOURCES_LENS ? 'lens' : 'plain';
      chips?.setView(view);
    },
    unsourced() {
      const keys = new Set([...(props?.unsourcedKeys() ?? []), ...(chips?.unsourcedKeys() ?? [])]);
      return keys.size;
    },
    dispose() {
      for (const off of detach) {
        off();
      }
      detach = [];
      core?.surface.remove('sites-danger');
      if (props) {
        core?.engine.scene.remove(props.group);
      }
      chips?.dispose();
      props?.dispose();
      danger?.dispose();
      chips = null;
      props = null;
      danger = null;
      core = null;
      built = '';
    },
  };
}

export const sites: ViewModule = createSites();
